import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  FiSave, FiUpload, FiTrash2, FiImage, FiUser, FiBriefcase, FiBook, 
  FiTarget, FiTrendingUp, FiX, FiPlus, FiCheckCircle, FiMail, FiPhone, FiMapPin 
} from 'react-icons/fi';
import { api } from '../../utils/api';
import { triggerRefresh, REFRESH_EVENTS } from '../../utils/refreshEvents';
import './AdminAbout.css';

const AdminAbout = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [about, setAbout] = useState({
    founder: {
      name: '',
      title: '',
      bio: '',
      image: '',
      email: '',
      phone: '',
      location: ''
    },
    journey: {
      introduction: '',
      experiences: []
    },
    education: {
      introduction: '',
      degrees: []
    },
    missions: {
      introduction: '',
      values: []
    },
    ambitions: {
      introduction: '',
      goals: []
    },
    cta: {
      title: 'Prêt à collaborer ?',
      description: 'Contactez-moi pour discuter de votre projet',
      buttonText: 'Me contacter',
      buttonLink: '/contact'
    }
  });
  const [founderImagePreview, setFounderImagePreview] = useState('');
  const [founderImageFile, setFounderImageFile] = useState(null);
  const [expandedSections, setExpandedSections] = useState({
    founder: true,
    journey: false,
    education: false,
    missions: false,
    ambitions: false,
    cta: false
  });

  useEffect(() => {
    fetchAbout();
  }, []);

  const fetchAbout = async () => {
    try {
      const res = await api.getAbout();
      setAbout(res.data);
      setFounderImagePreview(res.data.founder?.image || '');
    } catch (error) {
      console.error('Erreur:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    if (name.startsWith('founder.')) {
      const field = name.split('.')[1];
      setAbout(prev => ({
        ...prev,
        founder: {
          ...prev.founder,
          [field]: value
        }
      }));
    } else if (name.startsWith('cta.')) {
      const field = name.split('.')[1];
      setAbout(prev => ({
        ...prev,
        cta: {
          ...prev.cta,
          [field]: value
        }
      }));
    } else if (name.startsWith('journey.introduction')) {
      setAbout(prev => ({
        ...prev,
        journey: {
          ...prev.journey,
          introduction: value
        }
      }));
    } else if (name.startsWith('education.introduction')) {
      setAbout(prev => ({
        ...prev,
        education: {
          ...prev.education,
          introduction: value
        }
      }));
    } else if (name.startsWith('missions.introduction')) {
      setAbout(prev => ({
        ...prev,
        missions: {
          ...prev.missions,
          introduction: value
        }
      }));
    } else if (name.startsWith('ambitions.introduction')) {
      setAbout(prev => ({
        ...prev,
        ambitions: {
          ...prev.ambitions,
          introduction: value
        }
      }));
    }
  };

  const handleFounderImageSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        alert('Le fichier est trop volumineux. Taille maximale: 5MB');
        return;
      }
      
      if (!file.type.match(/^image\/(png|jpg|jpeg|svg|webp)$/)) {
        alert('Format de fichier non supporté. Formats acceptés: PNG, JPG, JPEG, SVG, WEBP');
        return;
      }

      setFounderImageFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setFounderImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleFounderImageUpload = async () => {
    if (!founderImageFile) {
      alert('Veuillez sélectionner un fichier');
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('image', founderImageFile);
      
      const res = await api.uploadFounderImage(formData);
      setAbout(prev => ({ 
        ...prev, 
        founder: { ...prev.founder, image: res.data.image } 
      }));
      setFounderImagePreview(res.data.image);
      setFounderImageFile(null);
      alert('Image uploadée avec succès !');
      triggerRefresh(REFRESH_EVENTS.ABOUT);
    } catch (error) {
      alert('Erreur lors de l\'upload: ' + (error.response?.data?.message || error.message));
    } finally {
      setUploading(false);
    }
  };

  const handleFounderImageDelete = async () => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer l\'image ?')) {
      return;
    }

    try {
      await api.deleteFounderImage();
      setAbout(prev => ({ 
        ...prev, 
        founder: { ...prev.founder, image: '' } 
      }));
      setFounderImagePreview('');
      setFounderImageFile(null);
      alert('Image supprimée avec succès !');
      triggerRefresh(REFRESH_EVENTS.ABOUT);
    } catch (error) {
      alert('Erreur lors de la suppression: ' + (error.response?.data?.message || error.message));
    }
  };

  // Gestion des expériences
  const addExperience = () => {
    setAbout(prev => ({
      ...prev,
      journey: {
        ...prev.journey,
        experiences: [...(prev.journey.experiences || []), {
          title: '',
          company: '',
          period: '',
          description: '',
          order: (prev.journey.experiences?.length || 0)
        }]
      }
    }));
  };

  const updateExperience = (index, field, value) => {
    setAbout(prev => {
      const experiences = [...(prev.journey.experiences || [])];
      experiences[index] = { ...experiences[index], [field]: value };
      return {
        ...prev,
        journey: { ...prev.journey, experiences }
      };
    });
  };

  const removeExperience = (index) => {
    setAbout(prev => ({
      ...prev,
      journey: {
        ...prev.journey,
        experiences: (prev.journey.experiences || []).filter((_, i) => i !== index)
      }
    }));
  };

  // Gestion des diplômes
  const addDegree = () => {
    setAbout(prev => ({
      ...prev,
      education: {
        ...prev.education,
        degrees: [...(prev.education.degrees || []), {
          title: '',
          institution: '',
          year: '',
          description: '',
          order: (prev.education.degrees?.length || 0)
        }]
      }
    }));
  };

  const updateDegree = (index, field, value) => {
    setAbout(prev => {
      const degrees = [...(prev.education.degrees || [])];
      degrees[index] = { ...degrees[index], [field]: value };
      return {
        ...prev,
        education: { ...prev.education, degrees }
      };
    });
  };

  const removeDegree = (index) => {
    setAbout(prev => ({
      ...prev,
      education: {
        ...prev.education,
        degrees: (prev.education.degrees || []).filter((_, i) => i !== index)
      }
    }));
  };

  // Gestion des valeurs
  const addValue = () => {
    setAbout(prev => ({
      ...prev,
      missions: {
        ...prev.missions,
        values: [...(prev.missions.values || []), {
          title: '',
          description: '',
          icon: 'FiCheckCircle',
          order: (prev.missions.values?.length || 0)
        }]
      }
    }));
  };

  const updateValue = (index, field, value) => {
    setAbout(prev => {
      const values = [...(prev.missions.values || [])];
      values[index] = { ...values[index], [field]: value };
      return {
        ...prev,
        missions: { ...prev.missions, values }
      };
    });
  };

  const removeValue = (index) => {
    setAbout(prev => ({
      ...prev,
      missions: {
        ...prev.missions,
        values: (prev.missions.values || []).filter((_, i) => i !== index)
      }
    }));
  };

  // Gestion des ambitions
  const addGoal = () => {
    setAbout(prev => ({
      ...prev,
      ambitions: {
        ...prev.ambitions,
        goals: [...(prev.ambitions.goals || []), {
          title: '',
          description: '',
          order: (prev.ambitions.goals?.length || 0)
        }]
      }
    }));
  };

  const updateGoal = (index, field, value) => {
    setAbout(prev => {
      const goals = [...(prev.ambitions.goals || [])];
      goals[index] = { ...goals[index], [field]: value };
      return {
        ...prev,
        ambitions: { ...prev.ambitions, goals }
      };
    });
  };

  const removeGoal = (index) => {
    setAbout(prev => ({
      ...prev,
      ambitions: {
        ...prev.ambitions,
        goals: (prev.ambitions.goals || []).filter((_, i) => i !== index)
      }
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    
    try {
      await api.updateAbout(about);
      alert('Informations mises à jour avec succès !');
      triggerRefresh(REFRESH_EVENTS.ABOUT);
    } catch (error) {
      alert('Erreur: ' + (error.response?.data?.message || error.message));
    } finally {
      setSaving(false);
    }
  };

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  if (loading) {
    return (
      <div className="loading-admin">
        <div className="loading-spinner"></div>
        <p>Chargement...</p>
      </div>
    );
  }

  const iconOptions = [
    { value: 'FiTarget', label: 'Cible' },
    { value: 'FiTrendingUp', label: 'Croissance' },
    { value: 'FiAward', label: 'Récompense' },
    { value: 'FiCheckCircle', label: 'Validation' },
    { value: 'FiBriefcase', label: 'Portefeuille' },
    { value: 'FiBook', label: 'Livre' },
    { value: 'FiUser', label: 'Utilisateur' }
  ];

  return (
    <div className="admin-about">
      <motion.div
        className="admin-header-section"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
      >
        <h2>Gestion de la page "À propos"</h2>
      </motion.div>

      <form onSubmit={handleSubmit} className="about-form">
        {/* Section Fondateur */}
        <motion.div
          className="settings-section"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.1 }}
        >
          <div className="section-header-toggle" onClick={() => toggleSection('founder')}>
            <h3>
              <FiUser className="section-icon" />
              Informations du Fondateur
            </h3>
            <span className="toggle-icon">{expandedSections.founder ? '−' : '+'}</span>
          </div>
          
          <AnimatePresence>
            {expandedSections.founder && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                transition={{ duration: 0.3 }}
              >
                <div className="logo-upload-section">
                  <div className="logo-preview-wrapper">
                    {founderImagePreview ? (
                      <div className="logo-preview-container">
                        <img src={founderImagePreview} alt="Photo du fondateur" className="logo-preview" />
                        <button
                          type="button"
                          onClick={handleFounderImageDelete}
                          className="logo-delete-btn"
                          title="Supprimer l'image"
                        >
                          <FiX />
                        </button>
                      </div>
                    ) : (
                      <div className="logo-placeholder">
                        <FiImage className="placeholder-icon" />
                        <span>Aucune image</span>
                      </div>
                    )}
                  </div>
                  
                  <div className="logo-upload-controls">
                    <label className="file-input-label">
                      <input
                        type="file"
                        accept="image/png,image/jpeg,image/jpg,image/svg+xml,image/webp"
                        onChange={handleFounderImageSelect}
                        className="file-input"
                      />
                      <span className="file-input-button">
                        <FiUpload />
                        Choisir une image
                      </span>
                    </label>
                    
                    {founderImageFile && (
                      <motion.button
                        type="button"
                        onClick={handleFounderImageUpload}
                        className="btn btn-primary"
                        disabled={uploading}
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                      >
                        {uploading ? 'Upload en cours...' : 'Uploader l\'image'}
                      </motion.button>
                    )}
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>Nom complet *</label>
                    <input
                      type="text"
                      name="founder.name"
                      value={about.founder.name}
                      onChange={handleChange}
                      required
                      placeholder="Nom du fondateur"
                    />
                  </div>
                  <div className="form-group">
                    <label>Titre / Fonction *</label>
                    <input
                      type="text"
                      name="founder.title"
                      value={about.founder.title}
                      onChange={handleChange}
                      required
                      placeholder="Ex: Chef de Projet, Assistante Indépendante"
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label>Biographie</label>
                  <textarea
                    name="founder.bio"
                    value={about.founder.bio}
                    onChange={handleChange}
                    rows="4"
                    placeholder="Biographie courte du fondateur..."
                  />
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label><FiMail /> Email</label>
                    <input
                      type="email"
                      name="founder.email"
                      value={about.founder.email}
                      onChange={handleChange}
                      placeholder="email@example.com"
                    />
                  </div>
                  <div className="form-group">
                    <label><FiPhone /> Téléphone</label>
                    <input
                      type="tel"
                      name="founder.phone"
                      value={about.founder.phone}
                      onChange={handleChange}
                      placeholder="07 12 34 56 78"
                    />
                  </div>
                  <div className="form-group">
                    <label><FiMapPin /> Localisation</label>
                    <input
                      type="text"
                      name="founder.location"
                      value={about.founder.location}
                      onChange={handleChange}
                      placeholder="France"
                    />
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>

        {/* Section Parcours Professionnel */}
        <motion.div
          className="settings-section"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          <div className="section-header-toggle" onClick={() => toggleSection('journey')}>
            <h3>
              <FiBriefcase className="section-icon" />
              Parcours Professionnel
            </h3>
            <span className="toggle-icon">{expandedSections.journey ? '−' : '+'}</span>
          </div>
          
          <AnimatePresence>
            {expandedSections.journey && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                transition={{ duration: 0.3 }}
              >
                <div className="form-group">
                  <label>Introduction</label>
                  <textarea
                    name="journey.introduction"
                    value={about.journey.introduction}
                    onChange={handleChange}
                    rows="3"
                    placeholder="Introduction au parcours professionnel..."
                  />
                </div>

                <div className="list-header">
                  <h4>Expériences professionnelles</h4>
                  <motion.button
                    type="button"
                    onClick={addExperience}
                    className="btn btn-secondary btn-small"
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    <FiPlus />
                    <span>Ajouter une expérience</span>
                  </motion.button>
                </div>

                {(about.journey.experiences || []).map((exp, index) => (
                  <div key={index} className="list-item-card">
                    <div className="list-item-header">
                      <h5>Expérience {index + 1}</h5>
                      <button
                        type="button"
                        onClick={() => removeExperience(index)}
                        className="btn-remove"
                      >
                        <FiTrash2 />
                      </button>
                    </div>
                    <div className="form-row">
                      <div className="form-group">
                        <label>Poste *</label>
                        <input
                          type="text"
                          value={exp.title}
                          onChange={(e) => updateExperience(index, 'title', e.target.value)}
                          required
                          placeholder="Ex: Chef de Projet"
                        />
                      </div>
                      <div className="form-group">
                        <label>Entreprise *</label>
                        <input
                          type="text"
                          value={exp.company}
                          onChange={(e) => updateExperience(index, 'company', e.target.value)}
                          required
                          placeholder="Nom de l'entreprise"
                        />
                      </div>
                      <div className="form-group">
                        <label>Période</label>
                        <input
                          type="text"
                          value={exp.period}
                          onChange={(e) => updateExperience(index, 'period', e.target.value)}
                          placeholder="Ex: 2020 - 2023"
                        />
                      </div>
                    </div>
                    <div className="form-group">
                      <label>Description</label>
                      <textarea
                        value={exp.description}
                        onChange={(e) => updateExperience(index, 'description', e.target.value)}
                        rows="3"
                        placeholder="Description des responsabilités..."
                      />
                    </div>
                  </div>
                ))}
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>

        {/* Section Études */}
        <motion.div
          className="settings-section"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.3 }}
        >
          <div className="section-header-toggle" onClick={() => toggleSection('education')}>
            <h3>
              <FiBook className="section-icon" />
              Études et Formations
            </h3>
            <span className="toggle-icon">{expandedSections.education ? '−' : '+'}</span>
          </div>
          
          <AnimatePresence>
            {expandedSections.education && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                transition={{ duration: 0.3 }}
              >
                <div className="form-group">
                  <label>Introduction</label>
                  <textarea
                    name="education.introduction"
                    value={about.education.introduction}
                    onChange={handleChange}
                    rows="3"
                    placeholder="Introduction aux études et formations..."
                  />
                </div>

                <div className="list-header">
                  <h4>Diplômes et formations</h4>
                  <motion.button
                    type="button"
                    onClick={addDegree}
                    className="btn btn-secondary btn-small"
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    <FiPlus />
                    <span>Ajouter un diplôme</span>
                  </motion.button>
                </div>

                {(about.education.degrees || []).map((degree, index) => (
                  <div key={index} className="list-item-card">
                    <div className="list-item-header">
                      <h5>Diplôme {index + 1}</h5>
                      <button
                        type="button"
                        onClick={() => removeDegree(index)}
                        className="btn-remove"
                      >
                        <FiTrash2 />
                      </button>
                    </div>
                    <div className="form-row">
                      <div className="form-group">
                        <label>Diplôme *</label>
                        <input
                          type="text"
                          value={degree.title}
                          onChange={(e) => updateDegree(index, 'title', e.target.value)}
                          required
                          placeholder="Ex: Master en Management"
                        />
                      </div>
                      <div className="form-group">
                        <label>Établissement *</label>
                        <input
                          type="text"
                          value={degree.institution}
                          onChange={(e) => updateDegree(index, 'institution', e.target.value)}
                          required
                          placeholder="Nom de l'établissement"
                        />
                      </div>
                      <div className="form-group">
                        <label>Année</label>
                        <input
                          type="text"
                          value={degree.year}
                          onChange={(e) => updateDegree(index, 'year', e.target.value)}
                          placeholder="Ex: 2020"
                        />
                      </div>
                    </div>
                    <div className="form-group">
                      <label>Description</label>
                      <textarea
                        value={degree.description}
                        onChange={(e) => updateDegree(index, 'description', e.target.value)}
                        rows="2"
                        placeholder="Description du diplôme..."
                      />
                    </div>
                  </div>
                ))}
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>

        {/* Section Missions */}
        <motion.div
          className="settings-section"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.4 }}
        >
          <div className="section-header-toggle" onClick={() => toggleSection('missions')}>
            <h3>
              <FiTarget className="section-icon" />
              Missions et Valeurs
            </h3>
            <span className="toggle-icon">{expandedSections.missions ? '−' : '+'}</span>
          </div>
          
          <AnimatePresence>
            {expandedSections.missions && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                transition={{ duration: 0.3 }}
              >
                <div className="form-group">
                  <label>Introduction</label>
                  <textarea
                    name="missions.introduction"
                    value={about.missions.introduction}
                    onChange={handleChange}
                    rows="3"
                    placeholder="Introduction aux missions et valeurs..."
                  />
                </div>

                <div className="list-header">
                  <h4>Valeurs</h4>
                  <motion.button
                    type="button"
                    onClick={addValue}
                    className="btn btn-secondary btn-small"
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    <FiPlus />
                    <span>Ajouter une valeur</span>
                  </motion.button>
                </div>

                {(about.missions.values || []).map((value, index) => (
                  <div key={index} className="list-item-card">
                    <div className="list-item-header">
                      <h5>Valeur {index + 1}</h5>
                      <button
                        type="button"
                        onClick={() => removeValue(index)}
                        className="btn-remove"
                      >
                        <FiTrash2 />
                      </button>
                    </div>
                    <div className="form-row">
                      <div className="form-group">
                        <label>Titre *</label>
                        <input
                          type="text"
                          value={value.title}
                          onChange={(e) => updateValue(index, 'title', e.target.value)}
                          required
                          placeholder="Ex: Excellence"
                        />
                      </div>
                      <div className="form-group">
                        <label>Icône</label>
                        <select
                          value={value.icon}
                          onChange={(e) => updateValue(index, 'icon', e.target.value)}
                        >
                          {iconOptions.map(opt => (
                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                          ))}
                        </select>
                      </div>
                    </div>
                    <div className="form-group">
                      <label>Description *</label>
                      <textarea
                        value={value.description}
                        onChange={(e) => updateValue(index, 'description', e.target.value)}
                        required
                        rows="3"
                        placeholder="Description de la valeur..."
                      />
                    </div>
                  </div>
                ))}
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>

        {/* Section Ambitions */}
        <motion.div
          className="settings-section"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.5 }}
        >
          <div className="section-header-toggle" onClick={() => toggleSection('ambitions')}>
            <h3>
              <FiTrendingUp className="section-icon" />
              Ambitions et Vision
            </h3>
            <span className="toggle-icon">{expandedSections.ambitions ? '−' : '+'}</span>
          </div>
          
          <AnimatePresence>
            {expandedSections.ambitions && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                transition={{ duration: 0.3 }}
              >
                <div className="form-group">
                  <label>Introduction</label>
                  <textarea
                    name="ambitions.introduction"
                    value={about.ambitions.introduction}
                    onChange={handleChange}
                    rows="3"
                    placeholder="Introduction aux ambitions et vision..."
                  />
                </div>

                <div className="list-header">
                  <h4>Objectifs et ambitions</h4>
                  <motion.button
                    type="button"
                    onClick={addGoal}
                    className="btn btn-secondary btn-small"
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    <FiPlus />
                    <span>Ajouter un objectif</span>
                  </motion.button>
                </div>

                {(about.ambitions.goals || []).map((goal, index) => (
                  <div key={index} className="list-item-card">
                    <div className="list-item-header">
                      <h5>Objectif {index + 1}</h5>
                      <button
                        type="button"
                        onClick={() => removeGoal(index)}
                        className="btn-remove"
                      >
                        <FiTrash2 />
                      </button>
                    </div>
                    <div className="form-group">
                      <label>Titre *</label>
                      <input
                        type="text"
                        value={goal.title}
                        onChange={(e) => updateGoal(index, 'title', e.target.value)}
                        required
                        placeholder="Ex: Expansion internationale"
                      />
                    </div>
                    <div className="form-group">
                      <label>Description *</label>
                      <textarea
                        value={goal.description}
                        onChange={(e) => updateGoal(index, 'description', e.target.value)}
                        required
                        rows="3"
                        placeholder="Description de l'objectif..."
                      />
                    </div>
                  </div>
                ))}
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>

        {/* Section CTA */}
        <motion.div
          className="settings-section"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.6 }}
        >
          <div className="section-header-toggle" onClick={() => toggleSection('cta')}>
            <h3>
              <FiCheckCircle className="section-icon" />
              Section Call-to-Action
            </h3>
            <span className="toggle-icon">{expandedSections.cta ? '−' : '+'}</span>
          </div>
          
          <AnimatePresence>
            {expandedSections.cta && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                transition={{ duration: 0.3 }}
              >
                <div className="form-group">
                  <label>Titre</label>
                  <input
                    type="text"
                    name="cta.title"
                    value={about.cta.title}
                    onChange={handleChange}
                    placeholder="Prêt à collaborer ?"
                  />
                </div>
                <div className="form-group">
                  <label>Description</label>
                  <textarea
                    name="cta.description"
                    value={about.cta.description}
                    onChange={handleChange}
                    rows="2"
                    placeholder="Contactez-moi pour discuter de votre projet"
                  />
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>Texte du bouton</label>
                    <input
                      type="text"
                      name="cta.buttonText"
                      value={about.cta.buttonText}
                      onChange={handleChange}
                      placeholder="Me contacter"
                    />
                  </div>
                  <div className="form-group">
                    <label>Lien du bouton</label>
                    <input
                      type="text"
                      name="cta.buttonLink"
                      value={about.cta.buttonLink}
                      onChange={handleChange}
                      placeholder="/contact"
                    />
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>

        <motion.div
          className="form-actions"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.7 }}
        >
          <motion.button
            type="submit"
            className="btn btn-primary"
            disabled={saving}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            <FiSave />
            <span>{saving ? 'Enregistrement...' : 'Enregistrer les modifications'}</span>
          </motion.button>
        </motion.div>
      </form>
    </div>
  );
};

export default AdminAbout;

